import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Key, ShieldCheck } from 'lucide-react';
import { apiService } from '../services/api';
import type { PublicPickupPinLookupResponse } from '../services/api/types/exclusive-ops';
import {
  extractPickupPinFromScan,
  looksLikePickupPin,
  normalizePickupPin,
} from '../utils/pickupPin';

function statusLabel(status?: string | null, valid?: boolean): string {
  if (status === 'ok' || (valid && !status)) return 'Valid pickup PIN';
  if (status === 'used') return 'Already used';
  if (status === 'expired') return 'Expired';
  if (status === 'invalid') return 'Invalid';
  return valid ? 'Valid pickup PIN' : 'Not verified';
}

export function VerifyPickupPage() {
  const { pin: pinParam } = useParams<{ pin: string }>();
  const [loading, setLoading] = useState(true);
  const [result, setResult] = useState<PublicPickupPinLookupResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  const code = normalizePickupPin(extractPickupPinFromScan(pinParam || ''));

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      setLoading(true);
      setError(null);
      setResult(null);
      if (!looksLikePickupPin(code)) {
        setError('This pickup QR / PIN is not valid.');
        setLoading(false);
        return;
      }
      try {
        const resp = await apiService.lookupPickupPinPublic(code);
        if (cancelled) return;
        setResult(resp.data ?? null);
      } catch {
        if (cancelled) return;
        // Soft fallback: verify endpoint (may mark used on some backends — last resort)
        try {
          const verify = await apiService.verifyPickupPin({ pin: code });
          if (cancelled) return;
          setResult({
            valid: Boolean(verify.data?.valid),
            status: verify.data?.status,
            student_id: verify.data?.student_id,
            student_name: verify.data?.student_name,
            admission_no: verify.data?.admission_no,
            guardian_name: verify.data?.guardian_name,
            message: verify.data?.message,
            expires_at: verify.data?.expires_at,
            used_at: verify.data?.used_at,
            pin_masked: code.replace(/.(?=.{2})/g, '•'),
          });
        } catch (err) {
          if (cancelled) return;
          setError(err instanceof Error ? err.message : 'Could not verify this pickup PIN.');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    void run();
    return () => {
      cancelled = true;
    };
  }, [code]);

  const valid = result?.valid === true && result?.status !== 'used' && result?.status !== 'expired';

  return (
    <div className="min-h-screen bg-secondary-50 flex items-start justify-center px-4 py-10">
      <div className="w-full max-w-lg bg-white border border-secondary-200 rounded-xl shadow-sm p-6 space-y-4">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-secondary-500 flex items-center gap-1.5">
            <Key className="h-3.5 w-3.5" />
            Gate pickup verification
          </p>
          <h1 className="text-xl font-semibold text-secondary-900 mt-1">
            {loading
              ? 'Checking…'
              : valid
                ? 'Pickup PIN verified'
                : statusLabel(result?.status, result?.valid)}
          </h1>
          <p className="text-sm text-secondary-600 mt-1">
            Gatekeepers should scan this QR in Security → Scan gate QR to release the student.
          </p>
        </div>

        {loading ? (
          <p className="text-sm text-secondary-600">Looking up pickup PIN…</p>
        ) : null}

        {error ? (
          <p className="text-sm text-rose-800 bg-rose-50 border border-rose-100 rounded-lg px-3 py-2">
            {error}
          </p>
        ) : null}

        {result ? (
          <div
            className={`rounded-lg border px-4 py-3 text-sm space-y-2 ${
              valid
                ? 'border-emerald-200 bg-emerald-50 text-emerald-950'
                : 'border-amber-200 bg-amber-50 text-amber-950'
            }`}
          >
            <p className="font-medium inline-flex items-center gap-1.5">
              {valid ? <ShieldCheck className="h-4 w-4" /> : null}
              {statusLabel(result.status, result.valid)}
            </p>
            {result.student_name ? (
              <p>
                <span className="text-secondary-600">Student:</span> {result.student_name}
              </p>
            ) : null}
            {result.admission_no ? (
              <p>
                <span className="text-secondary-600">Admission:</span> {result.admission_no}
              </p>
            ) : null}
            {(result.class_name || result.section_name) && (
              <p>
                <span className="text-secondary-600">Class:</span>{' '}
                {[result.class_name, result.section_name].filter(Boolean).join(' · ')}
              </p>
            )}
            {result.guardian_name ? (
              <p>
                <span className="text-secondary-600">Guardian:</span> {result.guardian_name}
              </p>
            ) : null}
            {result.message ? <p className="text-xs opacity-90">{result.message}</p> : null}
          </div>
        ) : null}

        <div className="pt-2 border-t border-secondary-100 text-sm text-secondary-600 space-y-2">
          <p>
            PIN shown in portal / WhatsApp is single-use. Opening this page does not release the
            student — the gatekeeper must complete scan & release in the Security app.
          </p>
          <Link to="/login" className="text-primary-700 underline">
            Staff login
          </Link>
        </div>
      </div>
    </div>
  );
}
